import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, HostListener, Input, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { gsap } from 'gsap';
import { CommonServiceService } from '../../../utilities/services/common-service/common-service.service';
import { MatchStatus } from '../../../models/common/match-status.enum';
import { resolveTableIdsForTieRow, TieRowMatch } from '../../../utilities/utils/match-sort.util';
import { environment } from '../../../../environments/environment';
import { isCourtBasedSportEnv, getServiceIcon } from '../../../shared/sport-terminology';
import { TranslateModule } from '@ngx-translate/core';
import { CategoryDisplayLabelPipe } from '../../../shared/category-ordering/category-display-label.pipe';
import { EVENT_TAB_ID } from '../../../utilities/event-landing-tab/event-landing-tab.constants';
import { TemplateService } from '../../../utilities/services/template/template.service';
import { TeamSubMatchDialogComponent } from '../../events/shared/team-sub-match-dialog/team-sub-match-dialog.component';
import { LogoToneDirective } from '../../../utilities/directive/logo_tone_directive/logo-tone.directive';

/** One participant side (single player or doubles pair) of a template-1 sub-match board. */
export interface T1SubMatchSide {
  readonly names: string[];
  readonly flags: string[];
  /** Per-player abbreviation chip text, parallel to `names`/`flags` (same index = same player). */
  readonly abbrs: string[];
  readonly score: number | string;
}

/** One board of a team tie, rendered as a footer progress dot and as a row in the "Sub-matches" dialog. */
export interface T1SubMatchTile {
  readonly id: number;
  readonly statusClass: 'live' | 'done' | 'wait';
  readonly isDoubles: boolean;
  readonly sideA: T1SubMatchSide;
  readonly sideB: T1SubMatchSide;
  readonly isWinnerA: boolean;
  readonly isWinnerB: boolean;
}

/**
 * Resolved display state for one side of a template-1 team-tie score row —
 * either the team's currently active board (singles or doubles, reference
 * `layoutTeamDoublesCard`/plain `.sc-side-info`) or, when no board is active
 * (tie not started / completed), the team identity itself.
 */
interface T1TeamSideView {
  /** True when the active board has two players — controls single vs. stacked player-line rendering under the team-name header. */
  readonly isDoubles: boolean;
  /** True when a specific board (singles or doubles) is active — triggers the team-name header + player-line(s) layout. False only when no board is active yet (tie not started/completed), where the team name is shown alone. */
  readonly hasActiveBoard: boolean;
  readonly teamName: string;
  /** Header above an active board's player line(s): every distinct selected parent (e.g. association and club), else the team name. */
  readonly parentLabel: string;
  readonly teamFlag: string;
  /** One name (singles board or no-active-board fallback) or two (doubles board). */
  readonly names: string[];
  /** Flag shown beside a single name line; unused when `isDoubles`. */
  readonly flag: string;
  /** Per-player flags, parallel to `names`; only populated when `isDoubles`. */
  readonly flags: string[];
  /** Abbreviation chip shown beside a single name line; unused when `isDoubles`. */
  readonly abbr: string;
  /** Per-player abbreviation chips, parallel to `names`; only populated when `isDoubles`. */
  readonly abbrs: string[];
  readonly score: number | string;
  readonly isWin: boolean;
}

@Component({
  selector: 'stupa-common-score-cards',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    CategoryDisplayLabelPipe,
    TeamSubMatchDialogComponent,
    LogoToneDirective,
  ],
  templateUrl: './common-score-cards.component.html',
  styleUrl: './common-score-cards.component.scss',
})
export class CommonScoreCardsComponent implements OnInit {
  @ViewChild('defaultContent') defaultContent!: ElementRef;
  @ViewChild('hoverContent') hoverContent!: ElementRef;

  @Input() item: any;
  screenWidth: any;
  @Input()
  show_arrow: boolean = false;
  /** `'classic'` = legacy card chrome; empty/default = premium layout (template 0). */
  @Input() variant: string = '';
  @Input() gradientalBackground: boolean = true;

  /** Open state for this card's template-1 "Sub-matches" dialog. */
  isSubDlgOpen = false;

  /** id of the sub-match tile currently previewed in the main score rows, or `null` for the tie-level default. */
  selectedSubMatchId: number | null = null;

  @HostListener('window:resize')
  onResize() {
    this.screenWidth = globalThis.window.innerWidth;
    this.cdr.detectChanges();
  }
  sport_id = environment.sport_id;
  readonly isCourtSurfaceSport = isCourtBasedSportEnv();
  readonly serviceIcon = getServiceIcon(environment.sport_id);
  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly router: Router,
    private readonly commonService: CommonServiceService,
    private readonly templateService: TemplateService
  ) {
    this.screenWidth = globalThis.window.innerWidth;
  }

  /** True when the OTT (template 1) card chrome should render — same data either way. */
  get isTemplate1(): boolean {
    return this.templateService.isTemplate1;
  }

  ngOnInit(): void {
    this.screenWidth = globalThis.window.innerWidth;
  }

  get_players_name(name: string) {
    if (!name) return '';

    if (name.includes('/')) {
      const players = name.split('/');

      return players
        .map((player) => {
          const nameParts = player.trim().split(' ');

          if (nameParts.length >= 2) {
            // Keep first name full, abbreviate only the last name
            const firstName = nameParts[0];
            const lastName = nameParts.at(-1);
            const middleNames = nameParts.slice(1, -1);

            let result = firstName;
            if (middleNames.length > 0) {
              result += ' ' + middleNames.join(' ');
            }
            result += ' ' + (lastName?.[0] ?? '');
            return result;
          } else {
            return player.trim();
          }
        })
        .join(' / ');
    }

    return name;
  }
  get_players_name2(name: string | { name: string }[], data: any): string {
    // CASE 1: Input is an array of player objects
    if (Array.isArray(name)) {
      return name
        .map((player) => {
          const fullName = player?.name?.trim() || '';
          const parts = fullName.split(' ');

          if (parts.length >= 2) {
            // Keep first name full, abbreviate only the last name
            const firstName = parts[0];
            const lastName = parts.at(-1);
            const middleNames = parts.slice(1, -1);

            let result = firstName;
            if (middleNames.length > 0) {
              result += ' ' + middleNames.join(' ');
            }
            result += ' ' + (lastName?.[0] ?? '');
            return result;
          } else {
            return fullName;
          }
        })
        .join(' / ');
    }

    // CASE 2: Input is a string with '/'
    if (typeof name === 'string' && name.includes('/')) {
      return name
        .split('/')
        .map((playerStr) => {
          const parts = playerStr.trim().split(' ');
          if (parts.length >= 2) {
            // Keep first name full, abbreviate only the last name
            const firstName = parts[0];
            const lastName = parts.at(-1);
            const middleNames = parts.slice(1, -1);

            let result = firstName;
            if (middleNames.length > 0) {
              result += ' ' + middleNames.join(' ');
            }
            result += ' ' + (lastName?.[0] ?? '');
            return result;
          } else {
            return playerStr.trim();
          }
        })
        .join(' / ');
    }

    // CASE 3: Single plain string
    return name as string;
  }

  playerName(name: string, mini: boolean) {
    if (!mini) {
      return name;
    }
    const parts = name?.split(' ');
    if (parts?.length >= 2) {
      // Keep first name full, abbreviate only the last name
      const firstName = parts[0];
      const lastName = parts.at(-1);
      const middleNames = parts.slice(1, -1);

      let result = firstName;
      if (middleNames?.length > 0) {
        result += ' ' + middleNames.join(' ');
      }
      result += ' ' + lastName?.[0];
      return result;
    }
    return name;
  }

  /**
   * Reference `playerAbbr()` port (LA48AF_1_updated.HTM) — a short 3-4 char
   * tag shown before a player/team name on the template-1 score card
   * (`.sc-abbr` chip). Prefers the last "word" of the name; falls back to
   * first+last initials when that's too short to be legible.
   *
   * @param name - Raw player/team display name.
   * @returns Uppercase abbreviation, or '' when no usable name is given.
   */
  playerAbbr(name: string | null | undefined): string {
    const trimmed = (name ?? '').trim();
    if (!trimmed) {
      return '';
    }
    const words = trimmed.replace(/\./g, ' ').split(/[\s/]+/).filter(Boolean);
    const last = words[words.length - 1] ?? trimmed;
    let abbr = last.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4);
    if (abbr.length < 3 && words.length > 1) {
      abbr = (words[0].slice(0, 2) + last.slice(0, 2))
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
        .slice(0, 4);
    }
    return (abbr || trimmed.slice(0, 4)).slice(0, 4).toUpperCase();
  }

  onHover() {
    if (!this.show_arrow) return;
    if (!this.defaultContent?.nativeElement || !this.hoverContent?.nativeElement) return;

    const overlay = this.hoverContent.nativeElement;
    overlay.style.display = 'flex';
    overlay.classList.add('show');
    gsap.fromTo(
      overlay,
      { opacity: 0 },
      { opacity: 1, duration: 0.22, ease: 'power1.out' }
    );
  }

  onLeave() {
    if (!this.show_arrow) return;
    if (!this.defaultContent?.nativeElement || !this.hoverContent?.nativeElement) return;

    this.hoverContent.nativeElement.classList.remove('show');

    const overlay = this.hoverContent.nativeElement;
    gsap.to(overlay, {
      opacity: 0,
      duration: 0.18,
      ease: 'power1.out',
      onComplete: () => {
        overlay.style.display = 'none';
      },
    });
  }

  /**
   * Opens the event this card's match belongs to, on its Draws & Results tab,
   * with the match's entry type, category and stage preselected.
   *
   * The tab's position in the event sidebar varies per tenant (other tabs get
   * filtered out), but the route segment is the tab's id — hence
   * {@link EVENT_TAB_ID}, never a literal. `stage_id` rides as a query param
   * because the events route has no stage segment.
   */
  navigate_to_scores() {
    if (!this.show_arrow) return;

    const liveScore = this.item?.live_score_json;
    const eventId = this.toId(liveScore?.event_id ?? this.item?.event_id);
    if (!eventId) {
      this.router.navigateByUrl('/scorecard');
      return;
    }

    const tabId = EVENT_TAB_ID.DRAWS_RESULTS;
    const categoryId = this.toId(liveScore?.category_id);
    const participantTypeId = this.toId(
      liveScore?.participant_type_id ?? this.item?.category?.participant_type_id,
    );
    const stageId = this.toId(liveScore?.stage_id);

    // Group matches hold ties for team events and plain matches otherwise, so
    // the tie's id is what locates the group — parent_match_id is null for singles.
    const matchId = this.toId(
      liveScore?.parent_match_id ?? liveScore?.match_id ?? this.item?.id,
    );

    const queryParams: Record<string, number> = {};
    if (stageId) {
      queryParams['stage_id'] = stageId;
    }
    if (matchId) {
      queryParams['match_id'] = matchId;
    }

    // Knockout renders a bracket rather than groups: the round id selects the
    // right round tab (Final, Semi Finals, ...) before the match is highlighted.
    const roundId = this.toId(this.item?.round_id ?? this.item?.round?.id);
    if (roundId) {
      queryParams['round_id'] = roundId;
    }

    this.router.navigate(
      ['/events', eventId, categoryId, participantTypeId, tabId, tabId],
      { queryParams },
    );
  }

  /**
   * Normalises a payload value to a positive numeric id.
   *
   * @param value - Raw id from the live-score payload.
   * @returns The id, or 0 when it is missing or not a usable number. 0 is also
   * the route's own "unspecified" value for category and entry type, so it can
   * be passed straight through.
   */
  private toId(value: unknown): number {
    const id = Number(value);
    return Number.isFinite(id) && id > 0 ? id : 0;
  }

  get_sub_match(data: any, sub_matches: any, type: any, index: number) {
    const teamName = data.participants[type - 1]?.participant_name;
    if (index === 0) {
      return teamName;
    }

    const participantName = this.resolveSubMatchParticipantName(data, sub_matches, type);
    return this.playerName(participantName, true);
  }

  private resolveSubMatchParticipantName(data: any, sub_matches: any, type: number): string {
    const targetMatch = data.live_score_json?.match_id;
    if (targetMatch == null || !Array.isArray(sub_matches) || sub_matches.length === 0) {
      return '';
    }

    const participants = sub_matches.find((match: any) => match.id === targetMatch)?.participants;
    if (!participants) {
      return '';
    }

    const team = type === 1 ? participants[0] : participants[1];
    return this.formatTeamParticipantName(team);
  }

  private formatTeamParticipantName(team: any): string {
    const details = team?.participant_details;
    if (!details?.length) {
      return '';
    }
    if (details.length === 1) {
      return details[0].name;
    }
    return details.map((player: any) => this.formatParticipantName(player.name)).join(' / ');
  }

  get_set_won(data: any, sub_matches: any, type: any) {
    if (!sub_matches?.length) {
      return;
    }

    const targetMatch = data.live_score_json?.match_id;
    if (targetMatch == null) {
      return;
    }

    const participants = sub_matches.find((match: any) => match.id === targetMatch)?.participants;
    if (!participants) {
      return;
    }

    const team = this.resolveTeamByType(participants, type);
    return team?.sets_won;
  }

  private resolveTeamByType(participants: any[], type: number): any {
    if (type === 1) {
      return participants[0];
    }
    if (type === 2) {
      return participants[1];
    }
    return null;
  }

  formatParticipantName(name: string): string {
    if (!name) return '';

    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      // Keep first name full, abbreviate only the last name
      const firstName = parts[0];
      const lastName = parts.at(-1);
      const middleNames = parts.slice(1, -1);

      let result = firstName;
      if (middleNames.length > 0) {
        result += ' ' + middleNames.join(' ');
      }
      result += ' ' + (lastName?.[0] ?? '');
      return result;
    }
    return name;
  }
  /**
   * Resolves the flag for a player by matching participant_details.user_role_id
   * with the corresponding selected_parents entry.
   */
  get_player_flag(
    selectedParents: Array<{
      user_role_id?: number | string;
      parent_flags?: string[];
    }> | null | undefined,
    player: { user_role_id?: number | string } | null | undefined,
    isBye = false,
  ): string {
    return this.commonService.get_selected_parents_flag_by_user_role(
      selectedParents,
      player?.user_role_id,
      isBye,
    );
  }

  /**
   * Resolves the API-provided Member Association / Club abbreviation
   * (selected_parents[].parent_abbr, joined as `MA/Club`) for a player by
   * matching participant_details.user_role_id with its selected_parents
   * entries — same lookup as get_player_flag, so doubles/team payloads never
   * mix one player's abbr with another's.
   * Returns '' when the API has no abbr for this player; callers fall back
   * to the name-derived playerAbbr() in that case.
   */
  get_player_abbr(
    selectedParents: Array<{
      user_role_id?: number | string;
      parent_abbr?: string;
      parent_role?: string;
    }> | null | undefined,
    player: { user_role_id?: number | string } | null | undefined,
    isBye = false,
  ): string {
    return this.commonService.get_selected_parents_abbr_by_user_role(
      selectedParents,
      player?.user_role_id,
      isBye,
    );
  }

  /** Returns true when a non-empty flag URL exists for the given player. */
  hasPlayerFlag(
    selectedParents: Array<{
      user_role_id?: number | string;
      parent_flags?: string[];
    }> | null | undefined,
    player: { user_role_id?: number | string } | null | undefined,
    isBye = false,
  ): boolean {
    const flagUrl = this.get_player_flag(selectedParents, player, isBye);
    return typeof flagUrl === 'string' && flagUrl.trim() !== '';
  }
  /**
   * Resolves whether the per-participant live score block (current set
   * score + card icons) should render. Keeps the original "actively live,
   * no winner yet" rule intact, and additionally shows/keeps updating the
   * block while the board is still on the live feed (`live_score === true`)
   * and its status is `IN_PROGRESS` or `SCORED` — covers the case where a
   * single (non-tie) match flips to `SCORED` and gets a `winner` before it
   * leaves the live feed, which previously hid the score immediately.
   *
   * @param data - Row item bound to the card.
   * @returns True when the live score block should be visible.
   */
  shouldShowLiveScoreBlock(data: any): boolean {
    const isStillLiveScoring =
      data?.live_score === true &&
      (data?.status === MatchStatus.IN_PROGRESS ||
        data?.status === MatchStatus.SCORED);

    return (
      (data?.live_score_json != 'winner announced' &&
        data?.live_score_json != null &&
        data?.winner == null) ||
      isStillLiveScoring
    );
  }

  /**
   * Resolves whether the board currently rendered by `item` has finished.
   * For a type-2 tie this checks the specific sub-match referenced by
   * live_score_json.match_id (the board this row represents) rather than
   * the parent tie's overall winner, which only gets set once every board
   * in the tie is done.
   *
   * @param data - Row item bound to the card.
   * @returns True when the represented board is SCORED (or the tie is won).
   */
  isMatchCompleted(data: any): boolean {
    if (data?.category?.participant_type_id === 2 && Array.isArray(data?.sub_matches)) {
      const targetMatchId = data.live_score_json?.match_id;
      const targetSubMatch = data.sub_matches.find(
        (sub: any) => sub.id === targetMatchId
      );
      if (targetSubMatch) {
        return targetSubMatch.status === MatchStatus.SCORED;
      }
    }
    return data?.winner != null;
  }

  /**
   * Resolves the table number shown on the card — prefers a type-2 board's
   * `meta.playing_table_id` for the matching board (by `match_id` / `__row_id`),
   * then a position-aware fallback — never `sub_matches[0]` blindly.
   *
   * @param data - Match row bound to the card.
   * @returns Table number when known, otherwise `undefined`.
   */
  gettables(data: TieRowMatch): number | undefined {
    const tableIds = resolveTableIdsForTieRow(data);
    const tableId = tableIds?.[0];
    return tableId !== undefined && Number.isFinite(tableId) ? tableId : undefined;
  }

  /**
   * Opens this card's "Sub-matches" dialog. Stops propagation so the footer
   * click doesn't also trigger the card's own navigate-to-scores.
   *
   * @param event - Footer click/keydown event.
   */
  openSubDlg(event: Event): void {
    event.stopPropagation();
    if (this.item?.category?.participant_type_id !== 2) {
      return;
    }
    this.isSubDlgOpen = true;
  }

  /** Closes this card's "Sub-matches" dialog. */
  closeSubDlg(): void {
    this.isSubDlgOpen = false;
  }

  /**
   * Previews one sub-match board's players/score in the card's main score
   * rows, driven by the footer progress dots. Purely an inline preview: the
   * full board-by-board table lives in the "Sub-matches" dialog.
   *
   * @param tile - Sub-match tile to preview.
   * @param event - Click event; stopped so it doesn't also open the dialog
   *   or trigger the card's own navigate-to-scores.
   */
  selectSubMatch(tile: T1SubMatchTile, event: Event): void {
    event.stopPropagation();
    this.selectedSubMatchId = tile.id;
  }

  /** Sub-match tile currently previewed in the main score rows, if any. */
  get t1SelectedSubMatchTile(): T1SubMatchTile | null {
    if (this.selectedSubMatchId == null) {
      return null;
    }
    return this.t1SubMatchTiles.find((tile) => tile.id === this.selectedSubMatchId) ?? null;
  }

  /** Side A view-model for a template-1 team-tie score row — see {@link resolveT1TeamSideView}. */
  get t1SideAView(): T1TeamSideView {
    return this.resolveT1TeamSideView(1);
  }

  /** Side B view-model for a template-1 team-tie score row — see {@link resolveT1TeamSideView}. */
  get t1SideBView(): T1TeamSideView {
    return this.resolveT1TeamSideView(2);
  }

  /**
   * Resolves the sub-match tile a team-tie card's main score row should
   * currently reflect: the manually previewed tile when one is selected
   * via the footer dots, otherwise the tie's live/active board
   * (`live_score_json.match_id` — reference's default main-stack content
   * before any tile is manually previewed). Looks up both against one
   * `t1SubMatchTiles` pass instead of two.
   *
   * @returns The tile to display, or `null` when none applies (not started / completed).
   */
  private get t1ActiveTile(): T1SubMatchTile | null {
    const tiles = this.t1SubMatchTiles;
    if (this.selectedSubMatchId != null) {
      const selected = tiles.find((tile) => tile.id === this.selectedSubMatchId);
      if (selected) {
        return selected;
      }
    }
    const matchId = this.item?.live_score_json?.match_id;
    if (matchId == null) {
      return null;
    }
    return tiles.find((tile) => tile.id === matchId) ?? null;
  }

  /**
   * Resolves what a team-tie card's main score row should display for one
   * side: the manually previewed sub-match tile when one is selected via
   * the footer dots, otherwise the tie's currently active board (reference
   * `layoutTeamDoublesCard` — singles board shows one player, doubles board
   * shows the team header + both players), falling back to the team's own
   * identity when no board is active (not started / completed).
   *
   * @param type - `1` for side A, `2` for side B.
   * @returns View-model for that side.
   */
  private resolveT1TeamSideView(type: 1 | 2): T1TeamSideView {
    const teamParticipant = this.item?.participants?.[type - 1];
    const teamName: string = teamParticipant?.participant_name ?? '';
    const parentLabel = this.selectedParentNames(teamParticipant?.selected_parents).join(' / ') || teamName;
    const teamFlag = this.get_player_flag(
      teamParticipant?.selected_parents,
      teamParticipant?.participant_details?.[0],
    );

    const tile = this.t1ActiveTile;
    if (tile) {
      const side = type === 1 ? tile.sideA : tile.sideB;
      return {
        isDoubles: tile.isDoubles,
        hasActiveBoard: true,
        teamName,
        parentLabel,
        teamFlag,
        names: side.names,
        flag: side.flags[0] ?? '',
        flags: side.flags,
        abbr: side.abbrs[0] ?? '',
        abbrs: side.abbrs,
        score: side.score,
        isWin: type === 1 ? tile.isWinnerA : tile.isWinnerB,
      };
    }

    const teamAbbr =
      this.get_player_abbr(teamParticipant?.selected_parents, teamParticipant?.participant_details?.[0]) ||
      this.playerAbbr(teamName);

    return {
      isDoubles: false,
      hasActiveBoard: false,
      teamName,
      parentLabel,
      teamFlag,
      names: [teamName],
      flag: teamFlag,
      flags: [],
      abbr: teamAbbr,
      abbrs: [],
      score: teamParticipant?.sets_won,
      isWin: this.item?.winner != null && this.item.winner === teamParticipant?.participant_id,
    };
  }

  private selectedParentNames(selectedParents: Array<{ parent_name?: string }> | null | undefined): string[] {
    const names = (selectedParents ?? [])
      .map((parent) => parent?.parent_name?.trim() ?? '')
      .filter((name) => name.length > 0);
    return [...new Set(names)];
  }

  /**
   * Builds the template-1 sub-match tile view models for a team tie's
   * footer progress dots from the same `item.sub_matches` the classic card already
   * reads via `get_sub_match`/`get_set_won` — no new data fetch. Includes
   * not-yet-started boards, shown as TBD, matching the reference design.
   *
   * @returns One tile per board on the tie.
   */
  get t1SubMatchTiles(): T1SubMatchTile[] {
    const subs: unknown[] = Array.isArray(this.item?.sub_matches) ? this.item.sub_matches : [];
    return subs.map((sub) => this.toT1SubMatchTile(sub as Record<string, unknown>));
  }

  private toT1SubMatchTile(sub: Record<string, unknown>): T1SubMatchTile {
    const matchType = sub['match_type'];
    const isDoubles = matchType == 3 || matchType == 4; // eslint-disable-line eqeqeq -- API sends numeric or string match_type
    const participants = (sub['participants'] as Record<string, unknown>[] | undefined) ?? [];
    const winner = sub['winner'];
    return {
      id: Number(sub['id']),
      statusClass: this.t1SubMatchStatusClass(sub['status']),
      isDoubles,
      sideA: this.toT1SubMatchSide(participants[0], isDoubles),
      sideB: this.toT1SubMatchSide(participants[1], isDoubles),
      isWinnerA: winner != null && winner === participants[0]?.['participant_id'],
      isWinnerB: winner != null && winner === participants[1]?.['participant_id'],
    };
  }

  private toT1SubMatchSide(participant: Record<string, unknown> | undefined, isDoubles: boolean): T1SubMatchSide {
    const details = (participant?.['participant_details'] as Record<string, unknown>[] | undefined) ?? [];
    const score = (participant?.['sets_won'] as number | undefined) ?? 0;
    const players = (isDoubles ? details.slice(0, 2) : details.slice(0, 1)).filter(
      (p) => typeof p['name'] === 'string' && (p['name'] as string).trim().length > 0,
    );
    if (!players.length) {
      return { names: ['TBD'], flags: [], abbrs: [], score };
    }

    type SelectedParents = Array<{ user_role_id?: number | string; parent_flags?: string[]; parent_abbr?: string }>;
    const teamParents = participant?.['selected_parents'] as SelectedParents | undefined;
    // Sub-match players carry their own selected_parents; the team-level list may not include every player's user_role_id.
    const parentsFor = (p: Record<string, unknown>): SelectedParents | undefined => {
      const own = p['selected_parents'] as SelectedParents | undefined;
      return own?.length ? own : teamParents;
    };
    // Built off the same filtered `players` list, one entry per player, so
    // names[i]/flags[i]/abbrs[i] always describe the same player — the flag
    // list previously dropped empty entries independently, which could shift
    // a later player's flag into an earlier player's slot.
    const names = players.map((p) => (p['name'] as string).trim());
    const flags = players.map((p) => this.get_player_flag(parentsFor(p), p as { user_role_id?: number | string }));
    const abbrs = players.map(
      (p, i) =>
        this.get_player_abbr(parentsFor(p), p as { user_role_id?: number | string }) || this.playerAbbr(names[i]),
    );
    return { names, flags, abbrs, score };
  }

  private t1SubMatchStatusClass(status: unknown): 'live' | 'done' | 'wait' {
    if (status === MatchStatus.IN_PROGRESS) {
      return 'live';
    }
    if (status === MatchStatus.SCORED) {
      return 'done';
    }
    return 'wait';
  }
}
